import { useEffect, useState, useMemo } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import { WearableDataDisplay } from "@/components/WearableDataDisplay";
import LimitationsSection from "@/components/profile/LimitationsSection";
import { Button } from "@/components/ui/button";
import { UserType } from "@/types/user";
import { ExtendedUser, ensureExtendedUser } from "@/types/encryption";
import { BasicUserType } from "@/types/user";
import Post from "@/components/Post";
import HealthGoals from "@/components/HealthGoals";
import LongevityAchievements from "@/components/LongevityAchievements";
import ProfileContentPicker from "@/components/ProfileContentPicker";
import { motion } from "framer-motion";
import { PostType } from "@/types/post";
import { getPosts, updatePostsByFilter } from "@/actions/posts/index";
import { createPost } from "@/actions/posts/mutations";
import { useToast } from "@/components/ui/use-toast";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@nextui-org/react";
import updateUser from "@/actions/updateUser";
import notify from "@/lib/notify";
import { follow, unfollow } from "@/actions/account";
import { getSocket, initSocket } from "@/socket";
import { MessageCircle, Plus, ArrowLeft, Home, User } from "lucide-react";
import { useRouter } from "next/navigation";
import { useWearableData } from "@/hooks/useWearableData";
import UserRecaps from "./recaps/UserRecaps";
import { CreatePostModal } from "@/components/modals/create-post-modal";
import AvatarPreview from "@/components/avatar/AvatarPreview";
import Mannequin3D from "@/components/avatar/Mannequin3D";
import { Avatar as AvatarType } from "@/types/avatar";
import { apiClient } from "@/lib/api-client";
import { useNavigation } from "@/hooks/useNavigation";

export default function ProfileComponent({
  user,
  modal,
  currentUser,
  showBackButton = false,
  onBack,
  onPostClick,
  onProfileClick,
}: {
  user: UserType;
  modal?: any;
  currentUser?: UserType;
  showBackButton?: boolean;
  onBack?: () => void;
  onPostClick?: (post: PostType) => void;
  onProfileClick?: (username: string, profile?: BasicUserType) => void;
}) {
  const [selectedContent, setSelectedContent] = useState("all");
  const [selectedSubTab, setSelectedSubTab] = useState("");
  const [timeFilter, setTimeFilter] = useState("24h");
  const [posts, setPosts] = useState<PostType[]>([]);
  const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(user?.image || undefined);
  const [name, setName] = useState(user?.name || "");
  const [username, setUsername] = useState(user?.username || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [userAvatars, setUserAvatars] = useState<AvatarType[]>([]);
  const [defaultAvatar, setDefaultAvatar] = useState<AvatarType | null>(null);
  const [loadingAvatars, setLoadingAvatars] = useState(false);

  const [isFollowing, setIsFollowing] = useState<boolean>(
    currentUser?.following?.find((f: string) => f === user?.id) ? true : false
  );

  const [followersCount, setFollowersCount] = useState(user?.followers?.length || 0);
  const [followingCount, setFollowingCount] = useState(user?.following?.length || 0);

  const { toast } = useToast();
  const { data: wearableData, loading: wearableLoading } = useWearableData(timeFilter);
  
  // Use navigation hook for consistent post navigation
  const {
    view,
    navigateToPost,
    navigateToProfile,
    findCachedPost,
    updateCachedPosts
  } = useNavigation();

  // Load user avatars - stabilized to prevent navigation interference
  useEffect(() => {
    let mounted = true;
    
    const loadAvatars = async () => {
      if (!user?.id || !mounted) return;
      
      try {
        setLoadingAvatars(true);
        // Add timeout to prevent hanging requests that could interfere with navigation
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Avatar loading timeout')), 5000)
        );
        
        const avatarPromise = apiClient.getAvatars();
        const response = await Promise.race([avatarPromise, timeoutPromise]) as { avatars: any[] };
        
        if (!mounted) return; // Prevent state update if component unmounted
        
        const avatars = response.avatars || [];
        setUserAvatars(avatars);
        
        // Find default avatar (assuming the first one or one marked as default)
        const defaultAv = avatars.find((avatar: AvatarType) => avatar.isDefault) || avatars[0] || null;
        setDefaultAvatar(defaultAv);
      } catch (error) {
        console.warn('Avatar loading failed (non-critical):', error);
        // Don't throw errors that could break navigation - just set empty state
        if (mounted) {
          setUserAvatars([]);
          setDefaultAvatar(null);
        }
      } finally {
        if (mounted) {
          setLoadingAvatars(false);
        }
      }
    };

    // Debounce the avatar loading to prevent rapid re-calls during navigation
    const timeoutId = setTimeout(loadAvatars, 100);
    
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [user?.id]);

  const handlePost = async (postData: any) => {
    try {
      const response = await createPost(postData);
      return response;
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  };

  const postUser = useMemo(() => {
    const baseUser = currentUser || user;
    const extended = ensureExtendedUser(baseUser);
    // Convert to BasicUserType with proper type conversion
    const basic: BasicUserType = {
      id: String(extended.id), // Convert number to string
      name: extended.name,
      username: extended.username,
      image: extended.image,
      email: extended.email,
      emailVerified: extended.emailVerified,
      hashedPassword: extended.hashedPassword || "",
      createdAt: extended.createdAt,
      updatedAt: extended.updatedAt,
      bio: extended.bio,
      following: Array.isArray(extended.following) ? extended?.following?.map(String) : [], // Convert number[] to string[]
      followers: Array.isArray(extended.followers) ? extended?.followers?.map(String) : [], // Convert number[] to string[]
      bioLink: extended.bioLink || "",
      profileURL: extended.profileURL || "",
      emailVerificationToken: extended.emailVerificationToken || null,
      emailVerificationTokenExpiry: extended.emailVerificationTokenExpiry || null,
      passwordResetToken: extended.passwordResetToken || null,
      passwordResetTokenExpiry: extended.passwordResetTokenExpiry || null,
      suspended: extended.suspended || false
    };
    return basic;
  }, [currentUser, user]);

  const router = useRouter();
  const socket = getSocket() || initSocket();

  useEffect(() => {
    if (!socket) return;

    // Define event handlers to prevent multiple registrations
    const handlePost = (post: PostType) => {
      if (post.userId === String(user?.id)) {
        setPosts(currentPosts => [post, ...currentPosts]);
      }
    };

    const handlePostDelete = ({ id }: { id: string }) => {
      setPosts(currentPosts => currentPosts.filter(post => post.id !== id));
    };

    const handleFollower = ({ target, count }: { target: string; count: number }) => {
      if (target === user?.id) {
        setFollowersCount(count);
      }
    };

    // Clean up any existing listeners first to prevent duplicates
    socket.off("post", handlePost);
    socket.off("post:delete", handlePostDelete);
    socket.off("follower", handleFollower);

    // Add the event listeners
    socket.on("post", handlePost);
    socket.on("post:delete", handlePostDelete);
    socket.on("follower", handleFollower);

    return () => {
      if (!socket) return;
      socket.off("post", handlePost);
      socket.off("post:delete", handlePostDelete);
      socket.off("follower", handleFollower);
    };
  }, [user?.id, socket]);

  useEffect(() => {
    let mounted = true;
    
    const loadPosts = async () => {
      if (!user?.id) return;
      
      try {
        const response = await getPosts({
          userId: user.id,
        });
        
        if (!mounted) return; // Prevent state update if component unmounted
        
        // Handle the new pagination structure
        const fetchedPosts = response.posts || [];
        
        if (!fetchedPosts.length) {
          setPosts([]);
          return;
        }
        
        setPosts(Array.isArray(fetchedPosts) ? fetchedPosts.sort((a: PostType, b: PostType) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ) : []);
      } catch (error) {
        console.error('Error fetching posts:', error);
        if (mounted) setPosts([]);
      }
    };
    
    // Use the actual ID value instead of the object reference
    const userId = user?.id;
    if (userId) {
      loadPosts();
    }
    
    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const handleFollowToggle = async () => {
    if (!currentUser || !socket) return;

    const extendedCurrentUser = ensureExtendedUser(currentUser);
    const extendedTargetUser = ensureExtendedUser(user);

    if (isFollowing) {
      setIsFollowing(false);
      setFollowersCount(followersCount - 1);

      socket.emit("followerEvent", {
        target: user?.id,
        count: followersCount - 1,
      });

      socket.emit("followingEvent", {
        target: currentUser.id,
        count: currentUser?.following?.length - 1,
      });

      await unfollow(extendedCurrentUser, String(extendedTargetUser.id));
    } else {
      setIsFollowing(true);
      setFollowersCount(followersCount + 1);

      await notify({
        userId: user?.id,
        type: "follow",
        message: `${currentUser!.username} is now following you!`,
      });

      socket.emit("followerEvent", {
        target: user?.id,
        count: followersCount + 1,
      });

      socket.emit("followingEvent", {
        target: currentUser.id,
        count: currentUser?.following?.length + 1,
      });

      await follow(extendedCurrentUser, String(extendedTargetUser.id));
    }
  };

  const renderContent = () => {
    // Simplified post click handler to match home page behavior
    const handlePostClick = (clickedPost: PostType) => {
      // Debug logging for post click
      console.log('📱 Profile post clicked:', {
        postId: clickedPost.id,
        username: clickedPost.username,
        currentView: view
      });
      
      // Direct navigation like home page - no complex finding logic
      navigateToPost(clickedPost, posts);
      
      console.log('📱 After navigateToPost called');
    };

    const handleProfileClick = (username: string, profile?: BasicUserType) => {
      // Use client-side navigation for profiles too
      navigateToProfile(username, profile);
      
      // Also call parent handler if provided (for compatibility)
      if (onProfileClick) {
        onProfileClick(username, profile);
      }
    };

    switch (selectedContent) {
      case "all":
        return posts?.map((post) => (
          <Post
            key={`profile-post-${post.id}`}
            {...post}
            currentUser={postUser}
            onPostClick={handlePostClick}
            onProfileClick={handleProfileClick}
          />
        ));
      case "social":
        return (
          <div className="space-y-6">
            <div className="flex gap-4 mb-4">
              <Button
                onClick={() => setIsCreatePostModalOpen(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create Post
              </Button>
              <Button
                variant="outline"
                onClick={() => router.push('/')}
                className="flex items-center gap-2"
              >
                <Home className="h-4 w-4" />
                Go Home
              </Button>
            </div>
            {selectedSubTab === "posts" && posts?.map((post) => (
              <Post
                key={`profile-post-${post.id}`}
                {...post}
                currentUser={postUser}
                onPostClick={handlePostClick}
                onProfileClick={handleProfileClick}
              />
            ))}
            {selectedSubTab === "replies" && (
              <p>Replies will be displayed here.</p>
            )}
            {selectedSubTab === "highlights" && (
              <p>Highlights will be displayed here.</p>
            )}
            {selectedSubTab === "articles" && (
              <p>Articles will be displayed here.</p>
            )}
            {selectedSubTab === "media" && (
              <p>Media content will be displayed here.</p>
            )}
            {selectedSubTab === "likes" && (
              <p>Liked content will be displayed here.</p>
            )}
          </div>
        );
      case "recaps":
        return (
          <div className="space-y-6">
            {wearableLoading ? (
              <div className="text-center py-8">Loading your health data...</div>
            ) : !wearableData || wearableData.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No wearable data available.</p>
                <p className="text-sm mt-2">
                  Connect your wearable device in the Wearables section to see your health data here.
                </p>
                <Button
                  onClick={() => router.push("/wearables")}
                  className="mt-4"
                >
                  Connect Wearable
                </Button>
              </div>
            ) : (
              <UserRecaps />
            )}
          </div>
        );
      case "protocols":
        return (
          <div className="space-y-6">
            {selectedSubTab === "medication" && (
              <p>Medication regimens will be displayed here.</p>
            )}
            {selectedSubTab === "diet" && (
              <p>Diet plans will be displayed here.</p>
            )}
            {selectedSubTab === "therapy" && (
              <p>Therapy protocols will be displayed here.</p>
            )}
          </div>
        );
      case "nutrition":
        return (
          <div className="space-y-6">
            {selectedSubTab === "meals" && (
              <p>Meal logs will be displayed here.</p>
            )}
            {selectedSubTab === "macros" && (
              <p>Macronutrient breakdown will be displayed here.</p>
            )}
            {selectedSubTab === "water" && (
              <p>Water intake tracking will be displayed here.</p>
            )}
          </div>
        );
      case "fitness":
        return (
          <div className="space-y-6">
            {selectedSubTab === "workouts" && (
              <p>Workout history will be displayed here.</p>
            )}
            {selectedSubTab === "steps" && (
              <p>Steps and walks will be displayed here.</p>
            )}
            {selectedSubTab === "yoga" && (
              <p>Yoga and stretching sessions will be displayed here.</p>
            )}
          </div>
        );
      case "biomarkers":
        return (
          <div className="space-y-6">
            {selectedSubTab === "wearables" && <WearableDataDisplay />}
            {selectedSubTab === "labs" && (
              <p>Labs data will be displayed here.</p>
            )}
          </div>
        );
      case "genome":
        return (
          <div className="space-y-6">
            {selectedSubTab === "disease" && (
              <p>Disease risk assessments will be displayed here.</p>
            )}
            {selectedSubTab === "pharma" && (
              <p>Pharmacogenomic information will be displayed here.</p>
            )}
            {selectedSubTab === "nutri" && (
              <p>Nutrigenomic insights will be displayed here.</p>
            )}
            {selectedSubTab === "fitness" && (
              <p>Fitness genetic traits will be displayed here.</p>
            )}
            {selectedSubTab === "carrier" && (
              <p>Carrier screening results will be displayed here.</p>
            )}
            {selectedSubTab === "behavioral" && (
              <p>Behavioral genetics insights will be displayed here.</p>
            )}
            {selectedSubTab === "ancestry" && (
              <p>Ancestry information will be displayed here.</p>
            )}
          </div>
        );
      case "orthotropics":
        return (
          <div className="space-y-6">
            {selectedSubTab === "analysis" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Orthotropics Analysis</h3>
                <p>Your detailed orthotropics analysis and assessments will be displayed here.</p>
              </div>
            )}
            {selectedSubTab === "measurements" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Key Measurements</h3>
                <p>Track your facial and oral measurements over time.</p>
              </div>
            )}
            {selectedSubTab === "exercises" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Daily Exercises</h3>
                <p>Your prescribed orthotropics exercises and routines.</p>
              </div>
            )}
            {selectedSubTab === "photos" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Progress Photos</h3>
                <p>Visual documentation of your orthotropics journey.</p>
              </div>
            )}
          </div>
        );
      case "limitations":
        return <LimitationsSection userId={user.id} />;
      case "plans":
        return (
          <div className="space-y-6">
            {selectedSubTab === "daily" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Today's Plan</h3>
                <p>Your personalized health and wellness plan for today.</p>
              </div>
            )}
            {selectedSubTab === "weekly" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Weekly Goals</h3>
                <p>Your health and wellness goals for this week.</p>
              </div>
            )}
            {selectedSubTab === "completion" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Plan Completion</h3>
                <p>Track how well you're following your action plans.</p>
              </div>
            )}
            {selectedSubTab === "history" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Plan History</h3>
                <p>Review your past action plans and their outcomes.</p>
              </div>
            )}
          </div>
        );
      case "challenges":
        return (
          <div className="space-y-6">
            {selectedSubTab === "overview" && (
              <div>
                <p>Summary of your current health and fitness challenges.</p>
              </div>
            )}
            {selectedSubTab === "created" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Created by You</h3>
                <p>Challenges you've created for the community.</p>
              </div>
            )}
            {selectedSubTab === "joined" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Joined Challenges</h3>
                <p>Challenges you're currently participating in.</p>
              </div>
            )}
            {selectedSubTab === "completed" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Completed Challenges</h3>
                <p>Challenges you've successfully finished.</p>
              </div>
            )}
            {selectedSubTab === "failed" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Incomplete Challenges</h3>
                <p>Challenges that weren't completed.</p>
              </div>
            )}
          </div>
        );
      case "awards":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4">Your Awards</h3>
            <p>Achievements and awards you've earned will be displayed here.</p>
          </div>
        );
      case "progress":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Health Score Trends</h3>
              <p>Your health score trends and improvements will be displayed here.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Achievement Timeline</h3>
              <p>Timeline of your health and wellness achievements.</p>
            </div>
          </div>
        );
      case "events":
        return (
          <div className="space-y-6">
            {selectedSubTab === "upcoming" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Upcoming Events</h3>
                <p>Your scheduled health and wellness events will be displayed here.</p>
              </div>
            )}
            {selectedSubTab === "past" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Past Events</h3>
                <p>History of events you've attended or organized.</p>
              </div>
            )}
            {selectedSubTab === "created" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Created Events</h3>
                <p>Events you've organized or created.</p>
              </div>
            )}
            {selectedSubTab === "saved" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Saved Events</h3>
                <p>Events you've bookmarked for later.</p>
              </div>
            )}
          </div>
        );
      case "limitations":
        return <LimitationsSection userId={user.id} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-8 relative">
      {showBackButton && (
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-900 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">{user?.name || user?.username}</h1>
            <p className="text-sm text-gray-400">{posts?.length || 0} posts</p>
          </div>
        </div>
      )}
      {modal && (
        <Modal
          isOpen={modal.isOpen}
          onOpenChange={modal.onOpenChange}
          className="bg-secondary"
          size="2xl"
        >
          <ModalContent>
            <ModalHeader className="text-xl">Edit your profile</ModalHeader>
            <ModalBody className="flex flex-col gap-3">
              <div className="flex justify-center mb-5">
                <div className="relative w-[100px] h-[100px]">
                  <Avatar className="w-full h-full">
                    <AvatarImage
                      src={avatarUrl}
                      alt={user?.name}
                      className="object-cover w-full h-full rounded-full"
                    />
                    <AvatarFallback className="bg-primary-foreground flex items-center justify-center">
                      <span className="text-xl">{user?.name?.[0] || '?'}</span>
                    </AvatarFallback>
                  </Avatar>
                </div>
              </div>

              <div className="relative">
                <input
                  className="hidden"
                  type="file"
                  id="file"
                  accept=".png,.jpeg,.jpg,.webp"
                  onChange={(e: any) => {
                    const file = e?.target?.files[0];
                    const reader = new FileReader();

                    reader.onload = async () => {
                      setAvatarUrl(reader.result as string);
                    };

                    reader.readAsDataURL(file);
                  }}
                />
                <Button
                  className="w-full px-auto"
                  onClick={() => {
                    const input = document.getElementById("file");
                    input?.click();
                  }}
                >
                  Edit profile picture
                </Button>
              </div>

              <input
                onChange={(e) => setName(e?.target?.value)}
                value={name}
                className="px-3 py-2 rounded-lg"
              />
              <input
                onChange={(e) => setUsername(e?.target?.value)}
                value={username}
                className="px-3 py-2 rounded-lg"
              />
              <input
                onChange={(e) => setBio(e?.target?.value)}
                value={bio}
                className="px-3 py-2 rounded-lg"
              />
            </ModalBody>
            <ModalFooter>
              <Button
                className="px-5"
                color="primary"
                onClick={async () => {
                  try {
                    await updateUser({
                      email: user?.email!,
                      name,
                      username,
                      bio,
                      image: avatarUrl
                    });

                    // Note: Post updates will be reflected on next page load
                    // since posts now fetch user data via JOIN in the backend
                    modal.onClose();
                    window?.location?.reload();
                  } catch (error) {
                    console.error('Error updating profile:', error);
                  }
                }}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
  <div className="md:col-span-2">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-4"
    >
      {/* Header with mannequin - flexbox within posts column */}
      <div className="flex items-start justify-between mb-4">
        {/* Left side - User info */}
        <div className="flex items-center space-x-4">
          <Avatar className="w-[50px] h-[50px] relative">
            <AvatarImage
              src={user?.image || undefined}
              alt={user?.name || 'User'}
              className="object-cover w-full h-full"
            />
            <AvatarFallback className="flex items-center justify-center">
              <span className="text-lg">{(user?.name && user?.name?.length > 0) ? user.name[0] : '?'}</span>
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold">{user?.name || 'Anonymous User'}</h1>
            <p className="text-muted-foreground">
              @{user?.username || (user?.name ? user?.name?.toLowerCase().replace(/\s+/g, '') : 'anonymous')}
            </p>
          </div>
        </div>
        
        {/* Right side - Mannequin within posts column */}
        <div className="flex-shrink-0 ml-4">
          <div className="w-24 h-24 lg:w-32 lg:h-32">
            <Mannequin3D
              showCreateButton={false}
              className="w-full h-full"
            />
          </div>
          {currentUser?.id === user?.id && (
            <Button
              onClick={() => router.push(`/${user.username}/avatar`)}
              variant="outline"
              size="sm"
              className="w-full text-xs mt-2"
            >
              Create Avatar
            </Button>
          )}
        </div>
      </div>
      
      <p className="mb-4">{user?.bio}</p>
      
      <div className="flex space-x-4 mb-4">
        <span>
          <strong>{followersCount}</strong> Followers
        </span>
        <span>
          <strong>{followingCount}</strong> Following
        </span>
      </div>

      {currentUser?.id !== user?.id && (
        <div className="flex items-center gap-4 mb-4">
          <Button
            onClick={() => {
              router.push(`/messages?userId=${user.id}`);
            }}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <MessageCircle className="h-4 w-4" />
            Message
          </Button>
          <Button
            onClick={handleFollowToggle}
            variant={isFollowing ? "outline" : "default"}
            size="sm"
          >
            {isFollowing ? "Unfollow" : "Follow"}
          </Button>
        </div>
      )}
      {modal ? (
        <Button className="mb-4" onClick={modal.onOpen}>
          Edit Profile
        </Button>
      ) : (
        <div>
          <Button
            className="mb-4"
            onClick={handleFollowToggle}
          >
            {isFollowing ? "Following" : "Follow"}
          </Button>
        </div>
      )}
    </motion.div>
    
    <div className="space-y-6">
      <ProfileContentPicker
        defaultValue={selectedContent}
        onValueChange={(value: string, subValue?: string, timeFilter?: string) => {
          setSelectedContent(value);
          if (subValue) setSelectedSubTab(subValue);
          if (timeFilter) setTimeFilter(timeFilter);
        }}
      />
      {renderContent()}
    </div>
  </div>
  
  <div className="md:col-span-1 space-y-6">
    <HealthGoals />
    <LongevityAchievements />
  </div>
</div>
      <CreatePostModal
        open={isCreatePostModalOpen}
        onOpenChange={setIsCreatePostModalOpen}
        user={postUser}
        onPost={async (post: any) => {
          try {
            const response = await handlePost(post);
            if (response) {
              setPosts([response, ...posts]);
              setIsCreatePostModalOpen(false);
              toast({
                title: "Post created successfully!"
              });
            }
          } catch (error) {
            console.error('Error creating post:', error);
            toast({
              title: "Failed to create post",
              description: "Please try again later",
              variant: "destructive"
            });
          }
        }}
      />
    </div>
  );
}
